import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import {
  StripeCreateCustomerPortalDocument,
  StripeCreateCustomerPortalQuery,
  StripeCreateCustomerPortalQueryVariables,
} from 'shared/types/graphql';
import { Button, Text, Center, VStack } from '@televet/kibble-ui';
import { useBillingInfo } from 'shared/hooks/useBillingInfo';
import { useHistory } from 'react-router-dom';
import { RouteBasePaths } from 'routes';

const BillingSettings = (): ReactElement => {
  const client = useApolloClient();
  const { toastMessagePop } = useCustomToast();
  const { getDisplayBilling } = useBillingInfo();
  const history = useHistory();

  const [showTryAgain, setShowTryAgain] = useState(false);

  const openPortal = useCallback(async (): Promise<void> => {
    setShowTryAgain(false);
    try {
      const { data } = await client.query<StripeCreateCustomerPortalQuery, StripeCreateCustomerPortalQueryVariables>({
        query: StripeCreateCustomerPortalDocument,
        variables: {
          data: { returnUrl: window.location.href?.replace('billing', 'general') },
        },
      });

      if (data.stripeCreateCustomerPortal?.url) {
        window.open(data.stripeCreateCustomerPortal.url, '_blank')?.focus();
        window.location.href = window.location.href.replace('billing', 'general');
      } else {
        toastMessagePop('Failed to open Stripe portal. Please try again.', STATUSES.error);
      }
    } catch (error) {
      toastMessagePop('Failed to open Stripe portal. Please try again.', STATUSES.error);
      console.error(error);
      setShowTryAgain(true);
    }
  }, [client, toastMessagePop]);

  useEffect(() => {
    getDisplayBilling().then((displayBilling) => {
      if (displayBilling) {
        openPortal();
      } else {
        history.push(RouteBasePaths.Settings);
      }
    });
  }, [history, getDisplayBilling, openPortal]);

  return (
    <Center h="100%">
      {showTryAgain ? (
        <VStack>
          <Text>Encountered an error opening the Stripe portal. Please try again.</Text>
          <Button onClick={(): Promise<void> => openPortal()}>Try Again</Button>
        </VStack>
      ) : (
        <LoadingSpinner size="xl" message="Redirecting..." />
      )}
    </Center>
  );
};

export default BillingSettings;
